import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AnswerMetadataFields from "./AnswerMetadataFields";
import { normalizeAnswerMetadata } from "../constants/answerMetadata";
import {
  ANSWER_TEMPLATE,
  ANSWER_TEMPLATE_OPTIONS,
} from "../constants/answerTemplates";

export default function NewAnswerDialog() {
  // 입력 상태를 목록과 분리해 타이핑할 때 저장된 답안 전체가 다시 렌더링되지 않게 합니다.
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [examSubject, setExamSubject] = useState("");
  const [newAnswerMetadata, setNewAnswerMetadata] = useState(() =>
    normalizeAnswerMetadata(),
  );
  const [timeLimitOption, setTimeLimitOption] = useState("");
  const [answerTemplate, setAnswerTemplate] = useState(
    ANSWER_TEMPLATE.LAW_SCHOOL_COUNCIL,
  );
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const navigate = useNavigate();

  const handleCreateAnswer = () => {
    // 기존 답안 작성 화면이 사용하는 HH:mm 형식으로 전달합니다.
    const timeLimit = `${String(hours).padStart(2, "0")}:${String(
      minutes,
    ).padStart(2, "0")}`;
    navigate("/answers/new", {
      state: {
        examSubject: examSubject.trim() || "새 답안지",
        timeLimitOption,
        timeLimit,
        answerTemplate,
        answerMetadata: newAnswerMetadata,
      },
    });
    onClose();
  };

  return (
    <>
      <Center mb={6}>
        <Button
          leftIcon={<FaPlus />}
          colorScheme="teal"
          variant="solid"
          size="lg"
          onClick={() => {
            setNewAnswerMetadata(normalizeAnswerMetadata());
            onOpen();
          }}
        >
          새 답안지 만들기
        </Button>
      </Center>
      {/* 새 답안지 만들기 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent
          my="4"
          maxH="calc(100dvh - 32px)"
          maxW="min(448px, calc(100vw - 32px))"
        >
          <ModalHeader>새 답안지 만들기</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb="4">
              <FormLabel>답안 제목</FormLabel>
              <Input
                placeholder="선택 입력 (기본: 새 답안지)"
                value={examSubject}
                onChange={(e) => setExamSubject(e.target.value)}
              />
            </FormControl>

            <Box mb="4">
              <AnswerMetadataFields
                value={newAnswerMetadata}
                onChange={setNewAnswerMetadata}
              />
            </Box>

            <FormControl mb="4">
              <FormLabel>제한시간 선택</FormLabel>
              <Select
                placeholder="제한시간 선택"
                value={timeLimitOption}
                onChange={(e) => setTimeLimitOption(e.target.value)}
              >
                <option value="none">없음</option>
                <option value="set">설정 시간</option>
                <option value="cumulative">누적 시간</option>
              </Select>
            </FormControl>

            <FormControl mb="4">
              <FormLabel>답안지 양식</FormLabel>
              <RadioGroup value={answerTemplate} onChange={setAnswerTemplate}>
                <Stack spacing="3">
                  {ANSWER_TEMPLATE_OPTIONS.map((option) => (
                    <Box
                      key={option.value}
                      borderWidth="1px"
                      borderRadius="md"
                      p="3"
                      borderColor={
                        answerTemplate === option.value
                          ? "blue.400"
                          : "gray.200"
                      }
                      bg={
                        answerTemplate === option.value ? "blue.50" : "white"
                      }
                    >
                      <Radio value={option.value} fontWeight="semibold">
                        {option.label}
                      </Radio>
                      <Text fontSize="xs" color="gray.600" mt="1" ml="6">
                        {option.description}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            {/* 설정 시간 옵션이 선택되었을 때 시간 입력란 표시 */}
            {timeLimitOption === "set" && (
              <FormControl mb="4">
                <FormLabel>제한시간 설정</FormLabel>
                <Grid
                  templateColumns="repeat(4, 1fr)"
                  gap={4}
                  alignItems="center"
                >
                  {/* 시 입력 */}
                  <NumberInput
                    max={23}
                    min={0}
                    value={hours}
                    onChange={(valueString) =>
                      setHours(parseInt(valueString))
                    }
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text textAlign="center">시간</Text>

                  {/* 분 입력 */}
                  <NumberInput
                    max={59}
                    min={0}
                    value={minutes}
                    onChange={(valueString) =>
                      setMinutes(parseInt(valueString))
                    }
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text textAlign="center">분</Text>
                </Grid>
              </FormControl>
            )}
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr="3" onClick={handleCreateAnswer}>
              완료
            </Button>
            <Button onClick={onClose}>취소</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
